import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Auth, Hub } from 'aws-amplify'
import { DateTime } from 'luxon'
import useSessionStore from '../Context/useSessionStore'
import { getMe, postFederatedSignUpService } from '../Services'
import { signOut } from './useAuth'
import { Mixpanel } from 'Utils/Mixpanel'
import { CURRENT_USER_STORAGE, PENDING_COMPANY_STORAGE } from 'Utils/const/localstorage'
import { DASHBAORD_ROUTER, LOGIN_ROUTER, PRICING_ROUTER } from 'constant/router'

// Amplify sets this localStorage flag after a hosted-UI / federated sign-in.
const HOSTED_UI_FLAG = 'amplify-signin-with-hostedUI'

const NO_ACCOUNT_MESSAGE =
  'We could not finish setting up your Pulse account from Google. Please try again or sign up with your email.'
const GENERIC_MESSAGE = 'We could not complete Google sign in. Please try again.'

/**
 * Completes the app session after the user is redirected back from the Cognito
 * hosted UI (e.g. "Continue with Google"). Amplify handles the token exchange on
 * its own, but the app still needs to load the Pulse profile (provisioning a
 * company + user on first federated login), populate the session store and route
 * the user in. Mount this once, high in the tree.
 */
const useOAuthRedirect = () => {
  const navigate = useNavigate()
  const updateUser = useSessionStore((state) => state.updateUser)
  const handledRef = useRef(false)

  useEffect(() => {
    let active = true

    const failAndRedirect = async (message) => {
      await signOut()
      if (!active) return
      updateUser(null)
      localStorage.removeItem(CURRENT_USER_STORAGE)
      navigate(LOGIN_ROUTER, { state: { error: message } })
    }

    // First federated login has no Pulse profile yet: provision it from the
    // verified id token, then re-read the profile.
    const provisionAccount = async () => {
      const company = sessionStorage.getItem(PENDING_COMPANY_STORAGE) || undefined
      sessionStorage.removeItem(PENDING_COMPANY_STORAGE)

      const body = { timezone: `GMT${DateTime.local().toFormat('ZZ')}` }
      if (company) body.company = company

      const result = await postFederatedSignUpService(body)
      if (!result?.success) {
        throw new Error(result?.message || NO_ACCOUNT_MESSAGE)
      }

      const retry = await getMe()
      return retry?.data
    }

    const completeFederatedLogin = async () => {
      if (handledRef.current) return
      handledRef.current = true

      try {
        const response = await getMe()
        let me = response?.data

        if (!active) return

        if (!me?.id) {
          try {
            me = await provisionAccount()
          } catch (error) {
            if (!active) return
            const message = error?.response?.data?.message || error?.message || NO_ACCOUNT_MESSAGE
            await failAndRedirect(message)
            return
          }
        }

        if (!active) return

        if (!me?.id) {
          await failAndRedirect(NO_ACCOUNT_MESSAGE)
          return
        }

        let cognitoUser = null
        try {
          cognitoUser = await Auth.currentAuthenticatedUser()
        } catch (_) {
          cognitoUser = null
        }

        const sessionUser = { ...me, ...(cognitoUser || {}) }

        let serialisedUser
        try {
          serialisedUser = JSON.stringify(sessionUser)
        } catch (_) {
          serialisedUser = JSON.stringify(me)
        }

        Mixpanel.people.set({
          $distinct_id: me.id,
          $email: me.email,
          $name: `${me.firstname} ${me.lastname}`,
          $first_name: me.firstname,
          $last_name: me.lastname
        })
        Mixpanel.identify(me.id)

        localStorage.setItem(CURRENT_USER_STORAGE, serialisedUser)
        await updateUser(sessionUser)

        if (!active) return
        navigate(me.company_is_active ? DASHBAORD_ROUTER : PRICING_ROUTER)
      } catch (error) {
        console.error('Google sign in error:', error)
        if (active) await failAndRedirect(GENERIC_MESSAGE)
      }
    }

    const handleAuthEvent = ({ payload: { event } }) => {
      if (event === 'cognitoHostedUI') completeFederatedLogin()
      if (event === 'cognitoHostedUI_failure') failAndRedirect(GENERIC_MESSAGE)
    }

    const unsubscribe = Hub.listen('auth', handleAuthEvent)

    // Catch-up: the hosted-UI event can fire before this listener is registered,
    // or the user may refresh the tab right after returning from Google.
    if (localStorage.getItem(HOSTED_UI_FLAG) === 'true' && !useSessionStore.getState().user) {
      Auth.currentAuthenticatedUser()
        .then(() => completeFederatedLogin())
        .catch(() => {})
    }

    return () => {
      active = false
      unsubscribe()
    }
  }, [navigate, updateUser])
}

export default useOAuthRedirect
